'use client'
import { createContext, useContext, useRef, useState, useCallback } from 'react';
import logger from '../utils/logger';

/**
 * グローバルAudioContext管理
 *
 * iOS Safariの自動再生制限を回避するため、Root Layoutで1つのAudioContextを管理。
 * 1回のユーザータップで初期化すれば、ページ遷移後も自動再生が可能になる。
 *
 * 使用方法:
 * 1. useAudioContext() でコンテキストを取得
 * 2. initAudioContext() をユーザー操作時に呼び出し
 * 3. isAudioInitialized で初期化済みかどうか確認
 * 4. audioContextRef.current でAudioContextインスタンスにアクセス
 */
const AudioContextGlobal = createContext(null);

export function AudioContextProvider({ children }) {
  const audioContextRef = useRef(null);
  const [isAudioInitialized, setIsAudioInitialized] = useState(false);

  /**
   * AudioContextを初期化（ユーザー操作時に呼び出す）
   * @returns {Promise<boolean>} 初期化成功かどうか
   */
  const initAudioContext = useCallback(async () => {
    // 既に初期化済みの場合
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      if (audioContextRef.current.state === 'suspended') {
        try {
          await audioContextRef.current.resume();
          logger.log('[AudioContextProvider] AudioContext resumed');
        } catch (e) {
          logger.warn('[AudioContextProvider] Failed to resume AudioContext:', e);
        }
      }
      setIsAudioInitialized(true);
      return true;
    }

    // 新規作成
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) {
        logger.error('[AudioContextProvider] AudioContext is not supported');
        return false;
      }

      audioContextRef.current = new AudioContextClass();

      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      setIsAudioInitialized(true);
      logger.log('[AudioContextProvider] AudioContext initialized (state:', audioContextRef.current.state, ')');
      return true;
    } catch (error) {
      logger.error('[AudioContextProvider] Failed to initialize AudioContext:', error);
      return false;
    }
  }, []);

  /**
   * AudioContextの状態を確認してrunning状態にする
   * @returns {Promise<boolean>} running状態になったかどうか
   */
  const ensureAudioContextRunning = useCallback(async () => {
    if (!audioContextRef.current) {
      return false;
    }

    const ctx = audioContextRef.current;

    if (ctx.state === 'suspended') {
      try {
        await ctx.resume();
      } catch (e) {
        logger.warn('[AudioContextProvider] Failed to resume:', e);
      }
    }

    // running状態になるまで少し待機
    let attempts = 0;
    while (ctx.state !== 'running' && attempts < 10) {
      await new Promise(resolve => setTimeout(resolve, 50));
      attempts++;
    }

    return ctx.state === 'running';
  }, []);

  /**
   * AudioContextをリセット（デバッグ用）
   */
  const resetAudioContext = useCallback(() => {
    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch (e) {
        // ignore
      }
      audioContextRef.current = null;
    }
    setIsAudioInitialized(false);
    logger.log('[AudioContextProvider] AudioContext reset');
  }, []);

  return (
    <AudioContextGlobal.Provider
      value={{
        audioContextRef,
        isAudioInitialized,
        initAudioContext,
        ensureAudioContextRunning,
        resetAudioContext
      }}
    >
      {children}
    </AudioContextGlobal.Provider>
  );
}

/**
 * グローバルAudioContextを使用するためのフック
 */
export function useAudioContext() {
  const context = useContext(AudioContextGlobal);
  if (!context) {
    throw new Error('useAudioContext must be used within an AudioContextProvider');
  }
  return context;
}
